import { useEffect, useRef, useState } from "react";
import { debug } from "@/lib/log";
import { X, Camera, Upload, AlertTriangle, Loader2, Search, RefreshCw, Hourglass, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { compressForOcr } from "@/lib/imageCompress";

const UPLOAD_ERR = "Image upload failed. Please try again or type the medicine name manually.";
const OCR_ERR = "لم نتمكن من قراءة اسم الدواء بوضوح. يرجى تصوير العلبة بإضاءة أفضل أو كتابة الاسم يدويًا.";
const COMPRESS_ERR = "Unable to process the image. Please try again with a clearer photo or type the medicine name manually.";

// Auto-open policy — three TRUSTED resolution paths may open a drug
// card without user confirmation:
//   • 'barcode'         — exact serial lookup, /api/drugs/barcode
//   • 'exact_unique'    — Gemini extracted full name equals ONE DB
//                          drug variant after normalisation
//   • 'verified_cache'  — a prior user (or an exact_unique / barcode
//                          resolution) has confirmed this image hash
//
// Every other path — Gemini fuzzy, DeepSeek pick, ambiguous_duplicate
// — MUST be confirmed by the user via 'هل تقصد؟'. See server.py
// _process_job for the backend-side gate.
// Module-level const → stable identity → safe useEffect dep list.
const AUTO_OPEN_METHODS = new Set(["barcode", "exact_unique", "verified_cache"]);

/**
 * Photo-OCR modal for medicine boxes with queue-backed processing.
 *
 * - Client compresses image (max 1200px, JPEG 0.7, adaptive quality down to ≤800KB).
 * - Submits to /api/drugs/ocr/submit → gets job_id → polls /api/drugs/ocr/status/{id}.
 * - Shows status: waiting (+ position), processing, completed, failed.
 * - On completion the user confirms/edits the extracted name then Search runs the normal text search.
 */
export default function PhotoOcrModal({ open, onClose, onConfirm }) {
    const cameraInputRef = useRef(null);
    const uploadInputRef = useRef(null);
    const pollTimerRef = useRef(null);
    const pollInFlightRef = useRef(false);
    const timingRef = useRef({});

    const [previewUrl, setPreviewUrl] = useState("");
    const [file, setFile] = useState(null);
    const [compressing, setCompressing] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    // Phase C — resolution + limits data returned by the OCR status endpoint.
    // Populated only after jobStatus becomes 'completed' (or 'gemini_limit_reached').
    const [resolutionMethod, setResolutionMethod] = useState(null);
    const [geminiLimits, setGeminiLimits] = useState(null);
    // image_hash from the status response — required by /ocr/verify when
    // the user confirms a 'هل تقصد؟' suggestion so we can persist their
    // choice to the verified cache (next upload of this photo auto-opens
    // straight to the drug card without any Gemini/DeepSeek call).
    const [imageHash, setImageHash] = useState("");
    // NO-AUTOOPEN POLICY — when the user says "الاسم غير صحيح" from the
    // multi-match panel we force the correction view (Case C), even though
    // we still have matches[]. Reset on modal open / reset / retry.
    const [wrongName, setWrongName] = useState(false);
    const autoOpenedRef = useRef(false);

    // job state
    const [jobId, setJobId] = useState("");
    const [jobStatus, setJobStatus] = useState(""); // "" | waiting | processing | completed | failed
    const [position, setPosition] = useState(0);
    const [health, setHealth] = useState("healthy"); // healthy | busy | overloaded | paused
    const [processingPhase, setProcessingPhase] = useState(""); // "" | local_ocr | gemini
    const [extracted, setExtracted] = useState("");
    const [readable, setReadable] = useState(null);
    const [matches, setMatches] = useState([]);
    const [confidence, setConfidence] = useState(0);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (!open) return;
        // reset
        setPreviewUrl("");
        setFile(null);
        setCompressing(false);
        setUploadPct(0);
        setJobId("");
        setJobStatus("");
        setPosition(0);
        setHealth("healthy");
        setExtracted("");
        setReadable(null);
        setMatches([]);
        setConfidence(0);
        setErrorMsg("");
        // State setters are guaranteed stable by React and do not belong in
        // the deps array; only `open` should retrigger the reset.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        // `pollTimerRef` is a mutable ref (stable identity by design); only
        // the current preview URL string should drive cleanup registration.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewUrl]);

    // Auto-open uses `AUTO_OPEN_METHODS` (module-level). Do not redeclare here.
    useEffect(() => {
        if (jobStatus !== "completed") return;
        if (autoOpenedRef.current) return;
        if (!AUTO_OPEN_METHODS.has(resolutionMethod)) return;
        if (!Array.isArray(matches) || matches.length !== 1) return;
        if (!(typeof confidence === "number" && confidence >= 0.9)) return;
        const single = matches[0];
        if (!single || !single.id) return;
        autoOpenedRef.current = true;
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollInFlightRef.current = false;
        debug(`[ocr] auto-open ${resolutionMethod} match (id=${single.id})`);
        // Gemini exact_unique needs a HUMAN confirmation before the row
        // enters the verified cache. We surface a persistent toast with
        // two actions after the drug card has opened. Barcode and
        // verified_cache are already trusted — no toast.
        if (resolutionMethod === "exact_unique" && jobId) {
            const drugName = single.name || single.name_en || "";
            toast(`تم التعرف على: ${drugName} — هل النتيجة صحيحة؟`, {
                duration: 15000,
                action: {
                    label: "نعم، صحيحة",
                    onClick: async () => {
                        try {
                            await fetch(`${API}/api/drugs/ocr/verify`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ job_id: jobId, drug_id: single.id }),
                            });
                            toast.success("تم حفظ التأكيد");
                        } catch (e) { toast.error("تعذّر حفظ التأكيد"); }
                    },
                },
                cancel: {
                    label: "النتيجة غير صحيحة",
                    onClick: () => { /* no cache write */ },
                },
            });
        }
        onConfirm(single);
    }, [jobStatus, matches, confidence, resolutionMethod, jobId, onConfirm]);

    // Safety net: any terminal jobStatus (completed / failed) MUST kill the
    // poll loop. Runs regardless of whether the modal auto-opens or the user
    // stays on the multi-match / correction UI.
    useEffect(() => {
        if (jobStatus !== "completed" && jobStatus !== "failed") return;
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollInFlightRef.current = false;
    }, [jobStatus]);

    if (!open) return null;

    const handleFile = async (f) => {
        if (!f) return;
        if (!["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"].includes(f.type) && !f.type.startsWith("image/")) {
            toast.error("الصيغة غير مدعومة. استخدم JPG أو PNG أو WEBP.");
            return;
        }
        setCompressing(true);
        setJobStatus("compressing");
        setExtracted("");
        setReadable(null);
        setMatches([]);
        setConfidence(0);
        setErrorMsg("");
        setUploadPct(0);
        timingRef.current = { compressStart: performance.now() };
        try {
            const compressed = await compressForOcr(f);
            timingRef.current.compressMs = Math.round(performance.now() - timingRef.current.compressStart);
            debug(`[ocr] compression: ${timingRef.current.compressMs}ms`);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(compressed));
            setFile(compressed);
            // "ready" = image already compressed and staged for immediate upload.
            // Submit button uploads this Blob as-is (compressForOcr is NOT re-run).
            setJobStatus("ready");
        } catch (e) {
            debug("compression error, falling back to original bytes:", e);
            // Fallback per spec #13 — never block the user. Backend re-encodes
            // + validates so a raw upload is still safe.
            try {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl(URL.createObjectURL(f));
                setFile(f);
                setJobStatus("ready");
                toast.warning("تعذّر ضغط الصورة، سيتم استخدام الصورة الأصلية.");
            } catch (e2) {
                debug("fallback preview error:", e2);
                setJobStatus("failed");
                setErrorMsg(COMPRESS_ERR);
                toast.error(COMPRESS_ERR);
            }
        } finally {
            setCompressing(false);
        }
    };

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollInFlightRef.current = false;
    };

    // Adaptive polling: start at 500ms, grow ~1.3x per tick up to 1200ms.
    // Single active timer; requests never overlap thanks to pollInFlightRef.
    const scheduleNextPoll = (id, currentDelay) => {
        if (!pollTimerRef.current && currentDelay === -1) return; // stopped
        const next = Math.min(1200, Math.max(500, Math.round(currentDelay * 1.3)));
        pollTimerRef.current = setTimeout(() => runPoll(id, next), next);
    };

    const runPoll = async (id, delay) => {
        if (pollInFlightRef.current) {
            // Previous poll still running; requeue at same delay
            pollTimerRef.current = setTimeout(() => runPoll(id, delay), delay);
            return;
        }
        pollInFlightRef.current = true;
        try {
            await pollStatus(id);
        } finally {
            pollInFlightRef.current = false;
        }
        // If pollStatus called stopPolling (completed/failed), timer is null → don't reschedule
        if (pollTimerRef.current !== null || delay > 0) {
            scheduleNextPoll(id, delay);
        }
    };

    const pollStatus = async (id) => {
        try {
            const res = await fetch(`${API}/drugs/ocr/status/${id}`);
            if (!res.ok) throw new Error("status_error");
            const data = await res.json();
            // Transition timings
            if (data.status === "processing" && !timingRef.current.processingStart) {
                timingRef.current.processingStart = performance.now();
                if (timingRef.current.queueStart) {
                    timingRef.current.queueMs = Math.round(performance.now() - timingRef.current.queueStart);
                    debug(`[ocr] queue wait: ${timingRef.current.queueMs}ms`);
                }
            }
            setJobStatus(data.status);
            setPosition(data.position || 0);
            if (data.health) setHealth(data.health);
            if (data.status === "completed") {
                stopPolling();
                if (timingRef.current.processingStart) {
                    timingRef.current.processingMs = Math.round(performance.now() - timingRef.current.processingStart);
                    debug(`[ocr] processing: ${timingRef.current.processingMs}ms`);
                }
                setExtracted(data.extracted_text || "");
                setReadable(!!data.readable);
                setMatches(Array.isArray(data.matches) ? data.matches : []);
                setConfidence(typeof data.confidence === "number" ? data.confidence : 0);
                setResolutionMethod(data.resolution_method || "gemini");
                setGeminiLimits(data.gemini_limits || null);
                setImageHash(typeof data.image_hash === "string" ? data.image_hash : "");
                if (!data.readable) setErrorMsg(OCR_ERR);
                // Feb 2026: when the OCR pipeline extracted a name but the
                // smart-search found no drug, auto-fire the parent text
                // search on the extracted text — the user should NEVER
                // see extracted text sitting untouched. The auto-open
                // useEffect handles the case where matches DO exist.
                if (
                    data.readable &&
                    (data.extracted_text || "").trim() &&
                    (!Array.isArray(data.matches) || data.matches.length === 0)
                ) {
                    debug("[ocr] no local match — auto-searching extracted text");
                    autoOpenedRef.current = true;
                    onConfirm((data.extracted_text || "").trim());
                    return;
                }
            } else if (data.status === "failed") {
                stopPolling();
                setReadable(false);
                setErrorMsg(data.message || OCR_ERR);
            } else if (data.status === "gemini_limit_reached") {
                stopPolling();
                setGeminiLimits(data.gemini_limits || null);
                setErrorMsg(data.message || "تم الوصول إلى الحد اليومي للبحث بالصور.");
            }
        } catch (err) {
            // Transient polling errors are expected while the job is being processed.
            debug("OCR status poll error:", err);
        }
    };

    // Feature-detect BarcodeDetector once and cache the result.
    // Falls back gracefully to normal OCR when the browser lacks native support.
    const tryReadBarcode = async (blob) => {
        try {
            if (typeof window === "undefined" || !("BarcodeDetector" in window)) return "";
            // 500ms hard timeout so a slow/hung detector never delays the upload.
            const detector = new window.BarcodeDetector({
                formats: ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "qr_code", "data_matrix"],
            });
            const bitmap = await createImageBitmap(blob).catch(() => null);
            if (!bitmap) return "";
            const detectPromise = detector.detect(bitmap).catch(() => []);
            const timeout = new Promise((resolve) => setTimeout(() => resolve([]), 500));
            const results = await Promise.race([detectPromise, timeout]);
            try { bitmap.close && bitmap.close(); } catch { /* ignore */ }
            const first = Array.isArray(results) && results.length ? results[0] : null;
            return (first && first.rawValue ? String(first.rawValue) : "").trim();
        } catch {
            return "";
        }
    };

    const submitOcr = async () => {
        if (!file) {
            toast.error("يرجى اختيار أو التقاط صورة أولًا");
            return;
        }
        setErrorMsg("");
        setJobStatus("uploading");
        setUploadPct(0);
        timingRef.current.uploadStart = performance.now();

        // ─── Barcode fast-path (client-side, $0 model cost) ────────────────
        // If the picture contains a readable barcode/QR/data-matrix AND the
        // exact code lives in the banned list, we short-circuit here — no
        // upload, no Gemini, no queue. Any failure silently falls through
        // to the normal OCR path so users never notice.
        try {
            const barcodeStart = performance.now();
            const code = await tryReadBarcode(file);
            timingRef.current.barcodeMs = Math.round(performance.now() - barcodeStart);
            if (code) {
                debug(`[ocr] barcode detected in ${timingRef.current.barcodeMs}ms → ${code}`);
                const res = await api.get(`/drugs/barcode`, { params: { code } });
                const data = res.data || {};
                if (data.is_banned && Array.isArray(data.results) && data.results.length > 0) {
                    const first = data.results[0];
                    setJobId(`barcode-${Date.now()}`);
                    setJobStatus("completed");
                    setExtracted(String(first.name || code));
                    setReadable(true);
                    setMatches([{
                        id: first.id,
                        name: first.name,
                        name_en: first.name_en || "",
                        similarity: 1.0,
                    }]);
                    setConfidence(1.0);
                    setResolutionMethod("barcode");
                    setGeminiLimits(null);
                    setUploadPct(100);
                    return;
                }
            }
        } catch (e) {
            debug("barcode precheck skipped:", e);
        }
        // ───────────────────────────────────────────────────────────────────

        try {
            const form = new FormData();
            form.append("file", file);
            const res = await api.post("/drugs/ocr/submit", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (evt.total) {
                        const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
                        setUploadPct(pct);
                    } else if (evt.loaded) {
                        setUploadPct((p) => Math.min(99, p + 5));
                    }
                },
            });
            timingRef.current.uploadMs = Math.round(performance.now() - timingRef.current.uploadStart);
            debug(`[ocr] upload: ${timingRef.current.uploadMs}ms (${file.size} bytes)`);
            const data = res.data;
            setJobId(data.job_id);
            setJobStatus(data.status);
            setPosition(data.position || 0);
            if (data.health) setHealth(data.health);
            setUploadPct(100);
            // Cache short-circuit: server returned a completed job with matches
            // already resolved (resolution_method="cache"). No polling needed.
            if (data.status === "completed") {
                debug(`[ocr] cache hit: no Gemini call needed`);
                stopPolling();
                // Fetch the matches via the same status endpoint the poller uses,
                // so we share exactly one response-shape handler.
                await pollStatus(data.job_id);
                return;
            }
            timingRef.current.queueStart = performance.now();
            stopPolling();
            // Adaptive polling: first tick after 500ms, grow to max 1200ms.
            pollTimerRef.current = setTimeout(() => runPoll(data.job_id, 500), 500);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 429) {
                const msg = typeof detail === "string"
                    ? detail
                    : "طلبات الصور كثيرة حاليًا. يرجى المحاولة بعد قليل.";
                setJobStatus("");
                setErrorMsg(msg);
                toast.error(msg);
                return;
            }
            if (status === 402) {
                // Server-side balance ledger is depleted (image search only).
                const msg = typeof detail === "string"
                    ? detail
                    : "تم نفاد رصيد البحث بالصور، يرجى المحاولة لاحقاً.";
                setJobStatus("");
                setErrorMsg(msg);
                toast.error(msg);
                return;
            }
            debug("upload error:", e);
            setJobStatus("failed");
            setErrorMsg(UPLOAD_ERR);
            toast.error(UPLOAD_ERR);
        }
    };

    const confirmSearch = () => {
        // If OCR is confident (>=0.8) and returned a validated DB match, pass
        // the whole match object so the caller can open it by id (no fuzzy
        // re-search). Otherwise pass the extracted string for the normal
        // text/fuzzy/DeepSeek pipeline.
        const confirmed = (confidence >= 0.8 && matches[0]) ? matches[0] : null;
        if (confirmed) {
            onConfirm(confirmed);
            return;
        }
        const val = extracted.trim();
        if (!val) {
            toast.error("يرجى تأكيد اسم الدواء قبل البحث");
            return;
        }
        onConfirm(val);
    };

    const reset = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        stopPolling();
        autoOpenedRef.current = false;
        setPreviewUrl("");
        setFile(null);
        setJobId("");
        setJobStatus("");
        setPosition(0);
        setExtracted("");
        setReadable(null);
        setMatches([]);
        setConfidence(0);
        setResolutionMethod(null);
        setGeminiLimits(null);
        setErrorMsg("");
        setUploadPct(0);
        setWrongName(false);
        timingRef.current = {};
    };

    const isRunning = ["uploading", "waiting", "processing"].includes(jobStatus);

    // Multi-match ordering: server already sorts by similarity desc, but
    // guarantee it here so the "هل تقصد" list stays consistent regardless
    // of upstream change. This is a pure derivation — no state writes.
    const sortedMatches = Array.isArray(matches)
        ? [...matches].sort((a, b) => (Number(b?.similarity) || 0) - (Number(a?.similarity) || 0))
        : [];

    // Reusable handler for the correction search — text-search endpoint via
    // parent (same pipeline as the top-bar search input). Never re-runs OCR
    // or Gemini. Empty input is rejected with a friendly toast.
    const runCorrectionSearch = () => {
        const val = (extracted || "").trim();
        if (!val) {
            toast.error("اكتب اسم الدواء أولاً");
            return;
        }
        onConfirm(val);
    };

    return (
        <div
            data-testid="photo-ocr-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="relative bg-white w-full max-w-lg rounded-2xl border-2 border-slate-200 shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b-2 border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                            <Camera className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">قراءة علبة الدواء</h3>
                            <p className="text-xs text-slate-500 mt-0.5">التقط أو ارفع صورة لواجهة العلبة</p>
                        </div>
                    </div>
                    <button
                        data-testid="photo-ocr-close-btn"
                        onClick={onClose}
                        className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
                        aria-label="إغلاق"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-5">
                    {/* Safety warning */}
                    <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 flex gap-3 items-start">
                        <AlertTriangle className="w-5 h-5 shrink-0 text-amber-700 mt-0.5" />
                        <div className="text-sm text-amber-900 leading-relaxed">
                            <p className="font-bold mb-1">تنبيه هام</p>
                            <p>القراءة الضوئية قد تكون <b>غير دقيقة</b>. يجب تأكيد اسم الدواء من العلبة أو استشارة الصيدلي.</p>
                        </div>
                    </div>

                    {/* Image picker or preview */}
                    {!previewUrl ? (
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                data-testid="photo-take-btn"
                                onClick={() => cameraInputRef.current?.click()}
                                disabled={compressing}
                                className="flex flex-col items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-8 rounded-xl transition-colors cursor-pointer disabled:opacity-60"
                            >
                                <Camera className="w-7 h-7" />
                                <span>التقاط بالكاميرا</span>
                            </button>
                            <button
                                type="button"
                                data-testid="photo-upload-btn"
                                onClick={() => uploadInputRef.current?.click()}
                                disabled={compressing}
                                className="flex flex-col items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-8 rounded-xl transition-colors cursor-pointer border-2 border-slate-200 disabled:opacity-60"
                            >
                                <Upload className="w-7 h-7" />
                                <span>رفع صورة</span>
                            </button>
                            <input
                                ref={cameraInputRef}
                                data-testid="photo-camera-input"
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                    // Capture BEFORE reset so retaking the
                                    // same photo still fires onChange (React
                                    // file inputs skip the event when value
                                    // is unchanged).
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    handleFile(f);
                                }}
                            />
                            <input
                                ref={uploadInputRef}
                                data-testid="photo-file-input"
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    handleFile(f);
                                }}
                            />
                            {compressing && (
                                <div className="col-span-2 flex items-center justify-center gap-2 text-sm text-slate-600 mt-1">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>جارٍ تجهيز الصورة...</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="relative rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50">
                                <img src={previewUrl} alt="preview" data-testid="photo-preview" className="w-full max-h-64 object-contain" />
                                {jobStatus === "ready" && (
                                    <div
                                        data-testid="photo-ready-badge"
                                        className="absolute top-2 left-2 flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        <span>تم تجهيز الصورة</span>
                                    </div>
                                )}
                            </div>
                            {!isRunning && jobStatus !== "completed" && (
                                <button
                                    data-testid="photo-change-btn"
                                    onClick={reset}
                                    className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-2.5 rounded-lg transition-colors cursor-pointer text-sm"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span>تغيير الصورة</span>
                                </button>
                            )}
                        </div>
                    )}

    {/* Submit OCR — always uses the pre-compressed Blob in `file` state. */}
    {previewUrl && (jobStatus === "" || jobStatus === "ready") && (
                        <button
                            data-testid="photo-run-ocr-btn"
                            onClick={submitOcr}
                            disabled={compressing || jobStatus !== "ready"}
                            className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-colors cursor-pointer disabled:opacity-60"
                        >
                            <Search className="w-5 h-5" />
                            <span>{compressing ? "جارٍ تجهيز الصورة..." : "استخراج اسم الدواء"}</span>
                        </button>
                    )}

                    {/* Status while uploading/queued/processing */}
                    {isRunning && (
                        <div
                            data-testid="ocr-status-panel"
                            className="border-2 border-slate-200 rounded-xl p-4 bg-slate-50 flex items-start gap-3"
                        >
                            <div className="w-10 h-10 shrink-0 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                                {jobStatus === "processing" ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : jobStatus === "uploading" ? (
                                    <Upload className="w-5 h-5" />
                                ) : (
                                    <Hourglass className="w-5 h-5" />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="text-sm font-bold text-slate-900" data-testid="ocr-phase-title">
                                    {jobStatus === "uploading" && "جارٍ رفع الصورة..."}
                                    {jobStatus === "waiting" && "في قائمة الانتظار"}
                                    {jobStatus === "processing" && processingPhase === "gemini" && "جارٍ التحقق المتقدم..."}
                                    {jobStatus === "processing" && processingPhase !== "gemini" && "جارٍ قراءة الصورة محلياً..."}
                                </div>
                                <div className="text-xs text-slate-600 mt-1">
                                    {jobStatus === "uploading" && `${uploadPct}%`}
                                    {jobStatus === "waiting" && position > 1 && `يوجد ${position - 1} طلب قبلك في الطابور`}
                                    {jobStatus === "waiting" && position === 1 && "طلبك هو التالي مباشرة"}
                                    {jobStatus === "processing" && processingPhase === "gemini" && "لم يتم التعرف محلياً، جارٍ استخدام النموذج المتقدم..."}
                                    {jobStatus === "processing" && processingPhase !== "gemini" && "قد تستغرق العملية بضع ثوانٍ..."}
                                </div>
                                {jobStatus === "waiting" && position > 1 && (health === "overloaded" || health === "paused") && (
                                    <div
                                        data-testid="ocr-overload-notice"
                                        className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 mt-2 font-bold leading-relaxed"
                                    >
                                        النظام يعالج طلبات كثيرة حاليًا، سيتم معالجة صورتك تلقائيًا عند توفر القدرة.
                                    </div>
                                )}
                                {jobStatus === "uploading" && (
                                    <div className="mt-2 w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            data-testid="upload-progress-bar"
                                            className="h-full bg-slate-900 transition-[width] duration-150"
                                            style={{ width: `${uploadPct}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Error banner (queue full, failure, etc.) */}
                    {errorMsg && jobStatus !== "completed" && (
                        <div
                            data-testid="ocr-error-banner"
                            className="border-2 border-red-400 bg-red-50 text-red-900 rounded-xl p-4 flex items-start gap-3"
                        >
                            <XCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
                            <p className="text-sm leading-relaxed">{errorMsg}</p>
                        </div>
                    )}

                    {/* Completed: confirmation dialog */}
                    {jobStatus === "completed" && (() => {
                        // Phase C — resolution + usage badge. Renders inline
                        // above the result cards. Never shows before we know
                        // whether Gemini actually ran on this job.
                        let badge = null;
                        if (resolutionMethod && resolutionMethod !== "gemini") {
                            badge = (
                                <div data-testid="ocr-no-gemini-badge" className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                    تمت المعالجة دون استخدام Gemini — لم يُحتسب طلب.
                                </div>
                            );
                        } else if (geminiLimits) {
                            const en = geminiLimits.enabled || {};
                            const anyEnabled = en.per_user || en.global || en.cost || en.balance;
                            if (!anyEnabled) {
                                badge = (
                                    <div data-testid="ocr-usage-open-badge" className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                        الاستخدام مفتوح — لا توجد حدود مفعّلة على Gemini.
                                    </div>
                                );
                            } else {
                                const used = en.per_user ? geminiLimits.used_today_per_user : geminiLimits.used_today_global;
                                const limit = en.per_user ? geminiLimits.limit_daily_per_user : geminiLimits.limit_daily_global;
                                const remaining = en.per_user ? geminiLimits.remaining_per_user : geminiLimits.remaining_global;
                                let resetH = "";
                                try {
                                    const d = new Date(geminiLimits.reset_at_riyadh || geminiLimits.reset_at_utc);
                                    if (!isNaN(d.getTime())) resetH = d.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" });
                                } catch { /* ignore */ }
                                badge = (
                                    <div data-testid="ocr-usage-remaining-badge" className="text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                        {`المتبقي اليوم: ${remaining ?? "-"} من ${limit ?? "-"}`}
                                        {resetH ? ` — يعاد الضبط ${resetH} بتوقيت الرياض` : ""}
                                    </div>
                                );
                            }
                        }
                        return (
                        <div className="space-y-3">
                            {badge}
                            {/* Case A — Single confident DB match: auto-open effect
                                will fire onConfirm(matches[0]). We render a brief
                                confirmation card while the parent switches views. */}
                            {readable && sortedMatches.length === 1 && confidence >= 0.9 && resolutionMethod === "barcode" ? (
                                <div
                                    data-testid="ocr-high-confidence"
                                    className="border-2 border-green-500 bg-green-50 rounded-xl p-4 flex items-start gap-3"
                                >
                                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-green-700" />
                                    <div className="flex-1">
                                        <div className="text-sm text-green-800 font-bold mb-1">
                                            تم العثور على الدواء عبر الباركود
                                        </div>
                                        <div className="text-sm text-green-900 font-bold">
                                            <span className="text-lg" dir="auto">{sortedMatches[0].name}</span>
                                            {sortedMatches[0].name_en && (
                                                <span className="text-sm text-green-800 mr-1" dir="ltr">({sortedMatches[0].name_en})</span>
                                            )}
                                        </div>
                                        <p className="text-sm text-green-900 mt-1">جارٍ فتح صفحة الدواء...</p>
                                    </div>
                                </div>
                            ) : readable && sortedMatches.length > 0 && !wrongName ? (
                                /* Case B — Gemini/cache results. NEVER auto-open (per
                                   NO-AUTOOPEN policy). User must confirm by clicking a
                                   suggestion, or reject via "الاسم غير صحيح؟". */
                                <div
                                    data-testid="ocr-multi-match"
                                    className="border-2 border-amber-400 bg-amber-50 rounded-xl p-4"
                                >
                                    <div className="text-sm text-amber-900 font-bold mb-1">
                                        هل تقصد أحد هذه الأدوية؟
                                    </div>
                                    <div className="text-xs text-amber-800 mb-3">
                                        اضغط الاسم الصحيح لعرض بطاقة الدواء. لن نفتح
                                        أي بطاقة قبل تأكيدك.
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {sortedMatches.slice(0, 5).map((m) => (
                                            <button
                                                key={m.id}
                                                type="button"
                                                data-testid={`ocr-match-${m.id}`}
                                                onClick={async () => {
                                                    autoOpenedRef.current = true;
                                                    // Persist user's pick → verified cache via secure endpoint.
                                                    // Server owns image_hash (looked up from job_id) — client
                                                    // never sends it. Fire-and-forget: opening the drug card
                                                    // must not depend on the cache write succeeding.
                                                    if (jobId && m.id) {
                                                        try {
                                                            await fetch(`${API}/api/drugs/ocr/verify`, {
                                                                method: "POST",
                                                                headers: { "Content-Type": "application/json" },
                                                                body: JSON.stringify({ job_id: jobId, drug_id: m.id }),
                                                            });
                                                        } catch (e) { /* ignore — cache-only */ }
                                                    }
                                                    onConfirm(m);
                                                }}
                                                className="w-full text-right inline-flex items-center justify-between gap-2 bg-white hover:bg-amber-100 border-2 border-amber-300 hover:border-amber-600 text-slate-900 font-bold px-4 py-2.5 rounded-xl transition-colors cursor-pointer"
                                            >
                                                <span className="flex flex-col items-start">
                                                    <span dir="auto">{m.name}</span>
                                                    {m.name_en && (
                                                        <span className="text-xs text-slate-500 font-medium" dir="ltr">{m.name_en}</span>
                                                    )}
                                                </span>
                                                <Search className="w-4 h-4 text-amber-700 shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                    {/* "الاسم غير صحيح؟" — routes the user to the
                                        manual correction input (Case C) without
                                        re-running OCR or Gemini. */}
                                    <button
                                        type="button"
                                        data-testid="ocr-wrong-name-btn"
                                        onClick={() => setWrongName(true)}
                                        className="mt-3 w-full text-center text-sm text-amber-900 hover:text-amber-950 underline underline-offset-2 font-bold py-2 rounded-lg cursor-pointer"
                                    >
                                        الاسم غير صحيح؟ صحّح الاسم يدويًا
                                    </button>
                                </div>
                            ) : (
                                /* Case C/D — OCR found no DB match. Show a correction
                                   input pre-filled with the extracted text (or empty
                                   if OCR read nothing) + a text-search button. Never
                                   re-runs OCR / Gemini. */
                                <div
                                    data-testid="ocr-correction-block"
                                    className="border-2 border-slate-300 bg-slate-50 rounded-xl p-4"
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-slate-600" />
                                        <div className="flex-1">
                                            <div className="text-sm text-slate-900 font-bold mb-1">
                                                {extracted
                                                    ? "الدواء غير موجود في قاعدة البيانات"
                                                    : "لم نتمكن من قراءة الاسم"}
                                            </div>
                                            <p className="text-xs text-slate-600">
                                                {extracted
                                                    ? "صحّح الاسم أدناه ثم اضغط بحث."
                                                    : "اكتب اسم الدواء يدويًا أو جرّب صورة أخرى."}
                                            </p>
                                        </div>
                                    </div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1" htmlFor="ocr-correction-input">
                                        صحح اسم الدواء
                                    </label>
                                    <input
                                        id="ocr-correction-input"
                                        data-testid="photo-ocr-result-input"
                                        type="text"
                                        value={extracted}
                                        onChange={(e) => setExtracted(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") runCorrectionSearch(); }}
                                        placeholder={extracted ? "" : "مثال: باراسيتامول"}
                                        className="w-full bg-white border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-3 text-slate-900 font-bold text-lg text-right"
                                    />
                                </div>
                            )}

                            {/* Action buttons — hidden for the barcode auto-open case
                                since the modal is about to close. Auto-open now only
                                fires for resolutionMethod==='barcode' (see effect above). */}
                            {!(readable && sortedMatches.length === 1 && confidence >= 0.9 && resolutionMethod === "barcode") && (
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        data-testid="photo-change-photo-btn"
                                        onClick={reset}
                                        className="flex-1 flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-3.5 rounded-xl transition-colors cursor-pointer"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        <span>تغيير الصورة</span>
                                    </button>
                                    {(sortedMatches.length === 0 || wrongName) && (
                                        <button
                                            type="button"
                                            data-testid="photo-corrected-search-btn"
                                            onClick={runCorrectionSearch}
                                            disabled={!extracted.trim()}
                                            className="flex-1 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-colors cursor-pointer disabled:opacity-60"
                                        >
                                            <Search className="w-4 h-4" />
                                            <span>بحث بالاسم المصحح</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        );
                    })()}

                    {/* Failed / unreadable retry */}
                    {jobStatus === "failed" && (
                        <button
                            type="button"
                            data-testid="photo-retry-btn"
                            onClick={reset}
                            className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-3.5 rounded-xl transition-colors cursor-pointer"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span>محاولة أخرى</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
